import { ConnectionStatus, DisplayOrientation } from "@workspace/mobile";
import React, { useState } from "react";
import { createCtx } from "./ContextHelper";
import { DEFAULT_PALETTE, storePalette, type Palette } from "../utils/palette";

interface DeviceContextProps {
  children: React.ReactNode;
}

declare type Theme = "Light" | "Dark" | "System";

interface DeviceContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** The colour palette chosen on this device (applied only while the feature is on). */
  palette: Palette;
  /** Choose a palette and remember it on this device. */
  setPalette: (palette: Palette) => void;
  /** Set what was already stored, without storing it again. */
  loadPalette: (palette: Palette) => void;
  lastSyncForDate?: Date;
  connectionStatus: ConnectionStatus;
  setConnectionStatus: (status: ConnectionStatus) => void;
  biometricEnabled: boolean;
  setBiometricEnabled: (biometricEnabled: boolean) => void;
  biometricSupported: boolean;
  setBiometricSupported: (biometricSupported: boolean) => void;
  pinSecure: boolean;
  setPinSecure: (pinSecure: boolean) => void;
  displayOrientation: DisplayOrientation;
  setDisplayOrientation: (orientation: DisplayOrientation) => void;
  colourScheme: "light" | "dark";
  isAccessButtonEnabled: boolean;
  setIsAccessButtonEnabled: (isAccessButtonEnabled: boolean) => void;
  accessButtonMenuItems: string;
  setAccessButtonMenuItems: (accessButtonMenuItems: string) => void;
}

export const [useDeviceContext, CtxProvider] = createCtx<DeviceContextType>();

export const DeviceContextProvider = ({ children }: DeviceContextProps) => {
  const [theme, setTheme] = React.useState<Theme>("Light");
  const [palette, setPalette] = React.useState<Palette>(DEFAULT_PALETTE);
  const [lastSyncForDate, setLastSyncForDate] = React.useState<Date>();
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("internet");
  const [biometricEnabled, setBiometricEnabled] =
    React.useState<boolean>(false);
  const [biometricSupported, setBiometricSupported] =
    React.useState<boolean>(false);
  const [pinSecure, setPinSecure] = React.useState<boolean>(false);
  const [displayOrientation, setDisplayOrientation] =
    React.useState<DisplayOrientation>("unknown");
  const [isAccessButtonEnabled, setIsAccessButtonEnabled] =
    React.useState<boolean>(false);
  const [accessButtonMenuItems, setAccessButtonMenuItems] =
    React.useState<string>("products");

  // Performance-optimized theme switching
  const optimizedSetTheme = React.useCallback((newTheme: Theme) => {
    // Add performance class to disable transitions during switch
    document.documentElement.classList.add("theme-switching");

    // Update theme state immediately
    setTheme(newTheme);

    // Update DOM attributes immediately
    document.body.setAttribute("data-theme", newTheme);

    // Apply dark mode class for Tailwind CSS immediately
    if (newTheme === "Dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    // Remove performance class after a very short delay to allow DOM updates
    setTimeout(() => {
      document.documentElement.classList.remove("theme-switching");
    }, 10);
  }, []);

  const choosePalette = React.useCallback((next: Palette) => {
    setPalette(next);
    storePalette(next);
  }, []);

  // No initial theme setup - let App.tsx handle theme application
  // This prevents the race condition where "Light" theme is applied before
  // the correct theme from storage is retrieved
  return (
    <CtxProvider
      value={{
        theme,
        setTheme: optimizedSetTheme,
        palette,
        setPalette: choosePalette,
        loadPalette: setPalette,
        lastSyncForDate,
        connectionStatus,
        setConnectionStatus: (status: ConnectionStatus) => {
          setConnectionStatus(status);
          if (status !== "internet") {
            setLastSyncForDate(new Date());
          }
        },
        biometricEnabled,
        setBiometricEnabled,
        biometricSupported,
        setBiometricSupported,
        pinSecure,
        setPinSecure,
        displayOrientation,
        setDisplayOrientation,
        colourScheme: theme === "Dark" ? "dark" : "light",
        isAccessButtonEnabled,
        setIsAccessButtonEnabled,
        accessButtonMenuItems,
        setAccessButtonMenuItems
      }}
    >
      {children}
    </CtxProvider>
  );
};
